import React, { useState } from 'react'
import { format as formatDate } from 'date-fns'
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Radio, RadioGroup, Stack, Switch, TextField,
  Typography
} from '@mui/material'
import StepCard from './StepCard'
import { SESSION_STATUS } from './cameraControlRules'

const nameOf = (session) => session.fullName || session.username

// C11.9, as the incident log: a browser Sun does not recognise comes as "Unknown" and is shown translated.
const deviceOf = (session, t_) => `${session.ip} · ${session.browser === 'Unknown' ? t_('Unknown') : session.browser}`

const sinceOf = (session) => {
  const since = new Date(session.since)
  return Number.isNaN(since.getTime()) ? '' : formatDate(since, 'HH:mm')
}

const YouMark = ({ t_ }) => (
  <Box component='span' sx={{ fontSize: 11, fontWeight: 700, color: '#1F1F1F', bgcolor: '#CFCFCF', borderRadius: 999, px: 0.875, py: 0.125 }}>
    {t_('You')}
  </Box>
)

/**
 * Story 213, reopening 4 (L2, C16.7, C16.10; design Main.dc.html v6, block «Sesiones») — who is in MIC and who is watched.
 * "Watch" / "Stop watching" apply at once (`onWatch`, `onStop`); with every slot taken (over the SAVED maximum), "Watch" asks
 * who stops being watched and makes one single change (RN143). The maximum and "Without vigilance, show protected content"
 * are settings of the page's bar; with All of MIC the switch does not apply (supuesto 5) and is dimmed.
 */
const SessionsBlock = ({
  enabled, sessions, storedMax, maxValue, maxInvalid, onMaxChange, showProtected, onShowProtected, allMic, dimmedOpacity, onWatch,
  onStop, t_
}) => {
  const [choosingFor, setChoosingFor] = useState('')
  const [picked, setPicked] = useState('')
  const watched = sessions.filter((session) => session.status === 'WATCHED')
  const choosing = sessions.find((session) => session.id === choosingFor)

  const watch = (session) => {
    if (watched.length < storedMax) {
      onWatch(session.id)
      return
    }
    setPicked('')
    setChoosingFor(session.id)
  }

  const change = () => {
    onWatch(choosingFor, picked)
    setChoosingFor('')
  }

  if (!enabled) {
    return (
      <StepCard id='camera-control-sessions' title={t_('Sessions')}>
        <Typography variant='body2' sx={{ color: '#A8A8A8' }}>{t_('No session is watched while camera control is deactivated')}</Typography>
      </StepCard>
    )
  }

  return (
    <StepCard
      id='camera-control-sessions'
      title={t_('Sessions')}
      action={(
        <Box component='span' data-testid='sessions-counter' sx={{ fontSize: 13, fontWeight: 700, color: 'primary.main', bgcolor: '#2B3A31', borderRadius: 999, px: 1.25, py: 0.375 }}>
          {t_('{{count}} of {{max}} watched', { count: watched.length, max: storedMax })}
        </Box>
      )}
    >
      <Stack direction='row' flexWrap='wrap' alignItems='center' gap={1.5} columnGap={3.5}>
        <TextField
          type='number'
          size='small'
          label={t_('Watched sessions at most')}
          value={maxValue}
          error={maxInvalid}
          onChange={(event) => onMaxChange(event.target.value)}
          inputProps={{ min: 1, max: 50, step: 1, inputMode: 'numeric' }}
          sx={{ width: 220 }}
        />
        <Stack data-testid='unwatched-setting' direction='row' alignItems='center' spacing={1} sx={{ opacity: allMic ? dimmedOpacity : 1 }}>
          <Switch
            checked={showProtected}
            disabled={allMic}
            onChange={(event) => onShowProtected(event.target.checked)}
            inputProps={{ role: 'switch', 'aria-label': t_('Without vigilance, show protected content') }}
          />
          <Stack>
            <Typography variant='body2' sx={{ color: 'text.primary' }}>{t_('Without vigilance, show protected content')}</Typography>
            <Typography variant='caption' sx={{ color: '#A8A8A8' }}>{t_('Sessions without a free slot or without a camera')}</Typography>
          </Stack>
        </Stack>
      </Stack>
      <Box component='ul' aria-label={t_('Open sessions')} sx={{ listStyle: 'none', m: 0, p: 0, borderRadius: '10px', bgcolor: '#383838', overflow: 'hidden' }}>
        {sessions.map((session) => {
          const status = SESSION_STATUS[session.status] || SESSION_STATUS.NONE
          return (
            <Box
              component='li'
              key={session.id}
              sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 2fr) 96px 110px 150px', alignItems: 'center', gap: 1.75, px: 2, py: 1.5, borderTop: '1px solid #444444' }}
            >
              <Stack sx={{ minWidth: 0 }}>
                <Stack direction='row' alignItems='center' spacing={1}>
                  <Typography data-testid='session-name' sx={{ fontWeight: 600, color: 'text.primary' }}>{nameOf(session)}</Typography>
                  {session.you === true && <YouMark t_={t_} />}
                </Stack>
                <Typography data-testid='session-user' variant='body2' sx={{ color: '#A8A8A8' }}>{session.username}</Typography>
              </Stack>
              <Typography data-testid='session-device' variant='body2' sx={{ color: '#CFCFCF', minWidth: 0 }}>{deviceOf(session, t_)}</Typography>
              <Typography data-testid='session-since' variant='body2' sx={{ color: '#CFCFCF' }}>{t_('Since {{time}}', { time: sinceOf(session) })}</Typography>
              <Box
                component='span'
                data-testid='session-status'
                sx={{ justifySelf: 'start', fontSize: 12, fontWeight: 700, borderRadius: 999, px: 1.25, py: 0.375, whiteSpace: 'nowrap', color: status.color, bgcolor: status.bgcolor, border: '1px solid', borderColor: status.border }}
              >
                {t_(status.text)}
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                {status.action === 'stop' && (
                  <Button variant='outlined' size='small' onClick={() => onStop(session.id)} sx={{ borderRadius: 999, color: '#E6E6E6', borderColor: '#6A6A6A', whiteSpace: 'nowrap' }}>
                    {t_('Stop watching')}
                  </Button>
                )}
                {status.action === 'watch' && (
                  <Button variant='outlined' size='small' onClick={() => watch(session)} sx={{ borderRadius: 999, whiteSpace: 'nowrap' }}>
                    {t_('Watch')}
                  </Button>
                )}
              </Box>
            </Box>
          )
        })}
      </Box>

      <Dialog open={choosingFor !== ''} onClose={() => setChoosingFor('')} aria-labelledby='camera-control-choose' maxWidth='xs' fullWidth>
        <DialogTitle id='camera-control-choose'>{t_('Choose who stops being watched')}</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5}>
            <Typography variant='body2' sx={{ color: '#CFCFCF' }}>{t_('To watch {{name}}', { name: choosing ? nameOf(choosing) : '' })}</Typography>
            <RadioGroup value={picked} onChange={(event) => setPicked(event.target.value)}>
              {watched.map((session) => (
                <FormControlLabel
                  key={session.id}
                  value={session.id}
                  control={<Radio />}
                  label={(
                    <Stack>
                      <Stack direction='row' alignItems='center' spacing={1}>
                        <span>{nameOf(session)}</span>
                        {session.you === true && <YouMark t_={t_} />}
                      </Stack>
                      <Typography variant='caption' sx={{ color: '#A8A8A8' }}>{deviceOf(session, t_)}</Typography>
                    </Stack>
                  )}
                />
              ))}
            </RadioGroup>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant='outlined' onClick={() => setChoosingFor('')} sx={{ borderRadius: 999 }}>{t_('Cancel')}</Button>
          {/* "Change" already exists as a noun («Cambio»): the button is its i18next context "action", the verb. */}
          <Button variant='contained' disabled={picked === ''} onClick={change} sx={{ borderRadius: 999 }}>{t_('Change', { context: 'action' })}</Button>
        </DialogActions>
      </Dialog>
    </StepCard>
  )
}

export default SessionsBlock
